import {
  BALL_RADIUS,
  simulateShot,
  type ArenaCapsule,
  type BallState,
  type HoleLayout,
  type Planet,
  type Target,
  type Vec2,
} from "@shared/physics";
import {
  buildBlockerHole,
  buildCapsuleChainArena,
  buildIntroHole,
  buildWavyRiverArena,
} from "@shared/course";
import { biomeForHole } from "@shared/biome";
import { setActiveTheme } from "../theme";
import type { SceneCtx } from "../scene";
import { buildHoleVisuals, updateTrail, type HoleVisuals } from "./render";
import { HoleCamera } from "./camera";
import { mulberry32 } from "@shared/rng";

export interface DemoHandle {
  dispose(): void;
}

const BALL_Y = BALL_RADIUS + 0.06;

// Slim attract-mode loop that runs behind the welcome menu. Builds a
// clean intro/blocker arena, fires shots from the rest position with
// random aim biased at the target, then rotates holes for variety. No
// HUD, no audio, no server traffic — drops the moment the player picks
// a real mode.
export function startDemo(scene: SceneCtx): DemoHandle {
  let alive = true;
  let visuals: HoleVisuals | null = null;
  let camera: HoleCamera | null = null;
  let hole: HoleLayout | null = null;
  let ball: BallState = { x: 0, z: 0, vx: 0, vz: 0 };
  // -1 so the first loadHole() bumps to 0 → intro hole on initial paint.
  // Iteration 2 lands on 1 (blocker), and iteration 3+ rolls the random
  // chain / river / ellipse mix.
  let holeIdx = -1;
  let shotsThisHole = 0;

  // Active replay window. While set, ball position is interpolated from
  // `samples`; once elapsed exceeds durationSec the ball is parked at the
  // rest position and we wait `nextFireAt` before firing again.
  let shot: {
    samples: { t: number; x: number; z: number }[];
    startedAt: number;
    durationSec: number;
    headIdx: number;
  } | null = null;
  let nextFireAt = 0;
  let pendingHoleSwap = false;

  function disposeVisuals() {
    if (visuals) {
      scene.scene.remove(visuals.root);
      visuals.dispose();
      visuals = null;
    }
  }

  function loadHole() {
    disposeVisuals();
    holeIdx++;
    shotsThisHole = 0;
    const seed = (Math.random() * 0xffffffff) >>> 0;
    // Pick from four layouts each cycle. The first two iterations always
    // show the simple intro/blocker shapes so the menu opens with
    // something readable, then the rotation diversifies into capsule
    // chains and wavy rivers — same arena variants the player will see
    // mid-round during Compete. Chain/river builders can fail (rare;
    // tee/target geometry rejected); when they do, we fall back to the
    // blocker shape so the demo never stalls.
    let built: HoleLayout | null = null;
    if (holeIdx <= 1) {
      built =
        holeIdx === 0
          ? buildIntroHole(holeIdx, seed)
          : buildBlockerHole(holeIdx, seed);
    } else {
      const r = Math.random();
      if (r < 0.3) built = buildIntroHole(holeIdx, seed);
      else if (r < 0.55) built = buildBlockerHole(holeIdx, seed);
      else if (r < 0.8) built = buildDemoChainHole(holeIdx, seed);
      else built = buildDemoRiverHole(holeIdx, seed);
    }
    hole = built ?? buildBlockerHole(holeIdx, seed);

    setActiveTheme(biomeForHole(`demo/${seed}`, holeIdx));
    scene.backdrop.applyTheme();
    visuals = buildHoleVisuals(hole);
    scene.scene.add(visuals.root);

    if (camera) {
      camera.setHole(hole, { intro: true });
    } else {
      camera = new HoleCamera(scene.camera, hole);
      camera.setHole(hole, { intro: true });
    }

    ball = { x: hole.tee.x, z: hole.tee.z, vx: 0, vz: 0 };
    visuals.ball.position.set(ball.x, BALL_Y, ball.z);
    updateTrail(visuals, [], 0);
  }

  function fireShot(t: number) {
    if (!hole || !visuals || !camera) return;
    const dx = hole.target.cx - ball.x;
    const dz = hole.target.cz - ball.z;
    const baseAngle = Math.atan2(dz, dx);
    // Wider jitter as we accumulate shots on the same hole — keeps the
    // demo from looking like the same straight line on repeat.
    const jitter =
      (Math.random() - 0.5) * (0.35 + shotsThisHole * 0.18);
    const angle = baseAngle + jitter;
    const power = 0.62 + Math.random() * 0.28;
    const aimX = Math.cos(angle);
    const aimZ = Math.sin(angle);
    const result = simulateShot(hole, ball, { aimX, aimZ, power });

    camera.beginShotCam(
      { x: ball.x, z: ball.z },
      { x: aimX, z: aimZ },
    );

    const samples = result.samples;
    const dur = samples[samples.length - 1]?.t ?? 0.4;
    shot = { samples, startedAt: t, durationSec: dur, headIdx: 0 };
    shotsThisHole++;

    // Sticky end-state — next shot fires from where this one settled.
    ball.x = result.endState.x;
    ball.z = result.endState.z;

    nextFireAt = t + dur + 0.6;
    if (
      result.outcome === "holed" ||
      result.outcome === "out_of_bounds" ||
      shotsThisHole >= 4
    ) {
      pendingHoleSwap = true;
    }
  }

  loadHole();
  nextFireAt = scene.clock.getElapsedTime() + 0.7;

  const off = scene.onFrame((t, _dt) => {
    if (!alive || !visuals || !hole || !camera) return;

    let bx = ball.x;
    let bz = ball.z;

    if (shot) {
      const elapsed = t - shot.startedAt;
      if (elapsed >= shot.durationSec) {
        // Shot finished — park at end-state, swap holes if requested.
        const last = shot.samples[shot.samples.length - 1]!;
        bx = last.x;
        bz = last.z;
        shot = null;
        if (pendingHoleSwap) {
          pendingHoleSwap = false;
          loadHole();
          nextFireAt = t + 0.6;
          return;
        }
      } else {
        // Bracket lookup — samples are sorted by t.
        const samples = shot.samples;
        let lo = 0;
        let hi = samples.length - 1;
        while (lo < hi - 1) {
          const mid = (lo + hi) >> 1;
          if (samples[mid]!.t <= elapsed) lo = mid;
          else hi = mid;
        }
        const a = samples[lo]!;
        const b = samples[hi]!;
        const span = Math.max(0.0001, b.t - a.t);
        const k = (elapsed - a.t) / span;
        bx = a.x + (b.x - a.x) * k;
        bz = a.z + (b.z - a.z) * k;
        // Walk the trail head forward in step with elapsed sim time so
        // the plasma trail grows behind the ball.
        while (
          shot.headIdx < samples.length - 1 &&
          samples[shot.headIdx + 1]!.t <= elapsed
        ) {
          shot.headIdx++;
        }
        const trailPoints: { x: number; y: number; z: number }[] = [];
        for (let i = 0; i <= shot.headIdx; i++) {
          const s = samples[i]!;
          trailPoints.push({ x: s.x, y: BALL_Y, z: s.z });
        }
        trailPoints.push({ x: bx, y: BALL_Y, z: bz });
        updateTrail(visuals, trailPoints, trailPoints.length);
      }
      camera.updateShotCamBall(bx, bz);
    } else if (t >= nextFireAt) {
      fireShot(t);
    }

    visuals.ball.position.set(bx, BALL_Y, bz);

    // Light per-frame ticks — only the things buildIntroHole /
    // buildBlockerHole actually produce. No gates, comets, wormholes,
    // wind, orbits, or pulsing target on these layouts.
    for (const p of visuals.planets) {
      p.rotation.y += (p.userData.spin ?? 0.02) * 0.02;
      const u = (p.material as { uniforms?: { uTime?: { value: number } } })
        .uniforms;
      if (u?.uTime) u.uTime.value = t;
    }
    const haloU = (visuals.targetHalo.material as {
      uniforms?: { uTime?: { value: number } };
    }).uniforms;
    if (haloU?.uTime) haloU.uTime.value = t;
    if (visuals.targetDiskMat.uniforms.uTime) {
      visuals.targetDiskMat.uniforms.uTime.value = t;
    }
    for (const m of visuals.targetExtraMats) {
      if (m.uniforms.uTime) m.uniforms.uTime.value = t;
    }
    const partU = (visuals.targetParticles.material as {
      uniforms?: { uTime?: { value: number } };
    }).uniforms;
    if (partU?.uTime) partU.uTime.value = t;
    visuals.targetLensRing.lookAt(scene.camera.position);
    for (const w of visuals.gravityWells) {
      const u = (w.material as { uniforms?: { uTime?: { value: number } } })
        .uniforms;
      if (u?.uTime) u.uTime.value = t;
    }
    if (visuals.boundaryMat.uniforms.uTime) {
      visuals.boundaryMat.uniforms.uTime.value = t;
    }
    visuals.boundaryParticles.update(t);

    camera.followBall(bx, BALL_Y, bz, 0.06, t);
  });

  return {
    dispose() {
      if (!alive) return;
      alive = false;
      off();
      disposeVisuals();
    },
  };
}

// Closest point on segment AB to P (clamped to [0,1]). Inlined here
// so demo.ts doesn't need to widen course.ts's exported surface.
function distToSegment(px: number, pz: number, s: ArenaCapsule): number {
  const dx = s.bx - s.ax;
  const dz = s.bz - s.az;
  const len2 = dx * dx + dz * dz || 1;
  const t = Math.max(0, Math.min(1, ((px - s.ax) * dx + (pz - s.az) * dz) / len2));
  const cx = s.ax + dx * t;
  const cz = s.az + dz * t;
  return Math.hypot(px - cx, pz - cz);
}

// Build a capsule-chain demo hole — 3-segment S-shape arena, 2-3
// planets sprinkled inside, no wormholes / gates / wind / comets so
// the slim per-frame tick still covers everything visible. Falls back
// to null on geometric edge cases (caller substitutes a blocker hole).
function buildDemoChainHole(idx: number, seed: number): HoleLayout | null {
  const rng = mulberry32(seed);
  const tee: Vec2 = { x: -34, z: (rng() - 0.5) * 6 };
  const target: Target = {
    cx: 34,
    cz: (rng() - 0.5) * 6,
    radius: 2.4,
  };
  const arena = buildCapsuleChainArena(rng, 0.55, tee, {
    x: target.cx,
    z: target.cz,
  });
  if (!arena) return null;
  const planets = placeDemoPlanets(rng, arena.segments, tee, target, 2);
  return {
    index: idx,
    seed,
    bounds: arena.bounds,
    arenaSegments: arena.segments,
    tee,
    target,
    planets,
    par: 3,
  };
}

// Build a wavy-river demo hole — 5-7 sinusoidal capsules following a
// bend, 2-3 planets inside the channel.
function buildDemoRiverHole(idx: number, seed: number): HoleLayout | null {
  const rng = mulberry32(seed);
  const tee: Vec2 = { x: -38, z: (rng() - 0.5) * 6 };
  const target: Target = {
    cx: 38,
    cz: (rng() - 0.5) * 6,
    radius: 2.4,
  };
  const arena = buildWavyRiverArena(rng, 0.7, tee, {
    x: target.cx,
    z: target.cz,
  });
  if (!arena) return null;
  const planets = placeDemoPlanets(rng, arena.segments, tee, target, 3);
  return {
    index: idx,
    seed,
    bounds: arena.bounds,
    arenaSegments: arena.segments,
    tee,
    target,
    planets,
    par: 3,
  };
}

// Drop `count` planets inside the capsule union, far enough from tee /
// target / each other to read clearly. Best-effort — bails after 60
// rejection-sampling rounds and returns whatever placed cleanly.
function placeDemoPlanets(
  rng: () => number,
  segments: ArenaCapsule[],
  tee: Vec2,
  target: Target,
  count: number,
): Planet[] {
  const planets: Planet[] = [];
  // Sample inside the bounding box of the segments and accept any point
  // sitting well inside at least one capsule.
  let minX = Infinity,
    maxX = -Infinity,
    minZ = Infinity,
    maxZ = -Infinity;
  for (const s of segments) {
    minX = Math.min(minX, s.ax - s.radius, s.bx - s.radius);
    maxX = Math.max(maxX, s.ax + s.radius, s.bx + s.radius);
    minZ = Math.min(minZ, s.az - s.radius, s.bz - s.radius);
    maxZ = Math.max(maxZ, s.az + s.radius, s.bz + s.radius);
  }
  for (let attempt = 0; attempt < 60 && planets.length < count; attempt++) {
    const cx = minX + rng() * (maxX - minX);
    const cz = minZ + rng() * (maxZ - minZ);
    let inside = false;
    for (const s of segments) {
      if (distToSegment(cx, cz, s) < s.radius - 4) {
        inside = true;
        break;
      }
    }
    if (!inside) continue;
    if (Math.hypot(cx - tee.x, cz - tee.z) < 7) continue;
    if (Math.hypot(cx - target.cx, cz - target.cz) < 5) continue;
    if (planets.some((p) => Math.hypot(cx - p.cx, cz - p.cz) < 6)) continue;
    planets.push({
      cx,
      cz,
      radius: 1.4 + rng() * 1.2,
      strength: 50 + rng() * 35,
    });
  }
  return planets;
}
